import type { ReportListItem } from "@/types/report-list"
import type { ReportResponse } from "@/types/report"
import type { Parameter } from "@/types/parameter"
import { deserializeParameters } from "@/lib/parameter-serializer"
import { GLOBAL_PARAMETER_NAMES } from "@/lib/constants"

/**
 * Filters report list items to only those belonging to a specific report list.
 * Used to scope selections to the active tab when performing actions like
 * preview, save PDF, or delete.
 */
export function filterItemsByReportList(
  items: ReportListItem[],
  reportListKey: string | undefined,
): ReportListItem[] {
  if (!reportListKey) return []
  return items.filter((item) => item.reportListKey === reportListKey)
}

/**
 * Returns true when a report list item references an inactive report.
 */
export function isReportListItemInactive(
  item: ReportListItem,
  reportResponses: ReportResponse[],
): boolean {
  const report = reportResponses.find((r) => r.reportKey === item.reportKey)
  return !!report?.inactiveDateTime
}

/**
 * Filters items so only executable (active) reports remain.
 */
export function filterExecutableReportListItems(
  items: ReportListItem[],
  reportResponses: ReportResponse[],
): ReportListItem[] {
  return items.filter((item) => !isReportListItemInactive(item, reportResponses))
}

/**
 * Returns true when a saved parameter value is missing from the values the
 * report currently offers for that dropdown.
 */
export function hasUnavailableParameterValues(item: ReportListItem): boolean {
  const parameters: Parameter[] | undefined = item.report?.parameters
  if (!parameters?.length || !item.reportListItemParameters) return false

  return (
    findParametersNeedingAttention(item.reportListItemParameters, parameters)
      .length > 0
  )
}

/**
 * Blanks selections the report no longer offers, so the parameter panel shows
 * them as nothing selected instead of a value the saved item never had.
 */
export function clearUnavailableParameterSelections(
  savedParametersXml: string,
  parameters: Parameter[],
): Parameter[] {
  const needsAttention = new Set(
    findParametersNeedingAttention(savedParametersXml, parameters),
  )
  if (needsAttention.size === 0) return parameters

  return parameters.map((parameter) => {
    if (!needsAttention.has(parameter.name)) return parameter
    if (parameter.type === "String") return { ...parameter, stringValue: "" }
    if (parameter.type === "MultiSelect") return { ...parameter, stringValues: [] }
    return parameter
  })
}

function findParametersNeedingAttention(
  savedParametersXml: string,
  parameters: Parameter[],
): string[] {
  return deserializeParameters(savedParametersXml).flatMap((entry) => {
    const parameter = parameters.find((p) => p.name === entry.parameterName)
    if (!parameter || !isCorrectableDropdown(parameter)) return []

    const savedValues = entry.selectedValues.filter(
      (value) => value.trim() !== "",
    )
    if (savedValues.length === 0) {
      return parameter.allowNull ? [] : [parameter.name]
    }

    const availableValues = new Set(
      parameter.availableValues.map((available) => String(available.value)),
    )
    return savedValues.some((value) => !availableValues.has(value))
      ? [parameter.name]
      : []
  })
}

/**
 * Only values the user can correct in the parameter panel are worth flagging.
 * This covers ProjectId, which is in GLOBAL_PARAMETER_NAMES and injected at run time.
 */
function isCorrectableDropdown(parameter: Parameter): boolean {
  if (parameter.availableValues.length === 0 || !parameter.isVisible) return false
  if (
    (GLOBAL_PARAMETER_NAMES as readonly string[]).includes(
      parameter.name.toLowerCase(),
    )
  ) {
    return false
  }

  return (
    parameter.type === "String" ||
    parameter.type === "MultiSelect" ||
    parameter.type === "Int"
  )
}
